import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';

import { fetchTrendSourceItems } from './service';
import type { TrendSourceItem } from './types';
import {
  getRunPresentationColor,
  getRunPresentationLabel,
  isLatestRunPresentation,
  sortByRunPresentationThenDate,
} from './runPresentationUtils';
import TrendEmptyState from './components/TrendEmptyState';
import { DISPLAY_FONT, TREND_COLORS } from './trendTheme';

const STATUS_OPTIONS = [
  { value: '', label: 'Alla statusar' },
  { value: 'normalized', label: 'Redo för analys' },
  { value: 'analyzed', label: 'Använd i analys' },
  { value: 'review', label: 'Kräver granskning' },
  { value: 'ignored', label: 'Bortsorterad' },
  { value: 'failed', label: 'Misslyckad' },
];

function formatDate(value?: string | null): string {
  if (!value) return 'Odaterad';
  return new Date(value).toLocaleDateString('sv-SE');
}

function sourceItemStatusLabel(value: string): string {
  if (value === 'fetched') return 'Hämtad';
  if (value === 'normalized') return 'Redo för analys';
  if (value === 'analyzed') return 'Använd i analys';
  if (value === 'review') return 'Kräver granskning';
  if (value === 'ignored') return 'Bortsorterad';
  if (value === 'failed') return 'Misslyckad';
  return value || 'Okänd';
}

export default function TrendsSourcesReaderPage() {
  const [items, setItems] = useState<TrendSourceItem[]>([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void fetchTrendSourceItems({ status: status || undefined, limit: 40 })
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      .catch(() => {
        if (!cancelled) setError('Kunde inte ladda källor och artiklar.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.6rem', fontWeight: 700, color: TREND_COLORS.navy }}>
          Hämtat material
        </Typography>
        <Typography sx={{ color: TREND_COLORS.textSecondary }}>
          Hitta originalkällor, se vad som har använts i analysen och öppna källmaterialet.
        </Typography>
      </Box>

      <TextField
        select
        SelectProps={{ native: true }}
        label="Status"
        value={status}
        onChange={(event) => setStatus(event.target.value)}
        sx={{ maxWidth: 260 }}
      >
        {STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </TextField>

      {status === 'review' && (
        <Alert severity="info">
          Källposter med status "Kräver granskning" är underlag som inte är säkra nog att använda vidare utan redaktionell kontroll. Öppna originalkällan här och gör artikelgranskningen i Redaktionellt.
        </Alert>
      )}
      {status === 'failed' && (
        <Alert severity="warning">
          Misslyckade källposter kunde inte hämtas eller normaliseras korrekt. Kontrollera originalkällan, källans inställningar eller kör om hämtningen efter åtgärd.
        </Alert>
      )}

      {loading && <CircularProgress sx={{ color: TREND_COLORS.navy }} />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <Stack spacing={1.5}>
          {items.length === 0 && (
            <TrendEmptyState
              dense
              title="Inga källposter matchar filtret"
              description="Prova en annan status eller kör en ny hämtning i Kontrollcenter."
            />
          )}
          {sortByRunPresentationThenDate(items).map((item) => (
            <Paper key={item._id} sx={{ p: 2 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>{item.title || 'Källpost utan rubrik'}</Typography>
                  {isLatestRunPresentation(item.runPresentation) && (
                    <Chip size="small" color={getRunPresentationColor(item.runPresentation)} label={getRunPresentationLabel(item.runPresentation)} />
                  )}
                  <Chip size="small" label={sourceItemStatusLabel(item.status)} />
                  <Chip size="small" label={item.provenance || 'feed'} variant="outlined" />
                  <Chip size="small" label={formatDate(item.publishedAt)} variant="outlined" />
                </Stack>
                <Typography color="text.secondary">
                  {item.originDomain || item.url}
                </Typography>
                {item.cleanedText && (
                  <Typography variant="body2" color="text.secondary">
                    {item.cleanedText.slice(0, 260)}{item.cleanedText.length > 260 ? '...' : ''}
                  </Typography>
                )}
                {item.url && (
                  <Box>
                    <Button size="small" href={item.url} target="_blank" rel="noreferrer">
                      Öppna originalkälla
                    </Button>
                  </Box>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
